import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import './FlavorSelectionModal.css';

const FlavorSelectionModal = ({ 
    show, 
    onHide, 
    product, 
    onConfirm,
    maxFlavors = 1,
    shopBranchId = null,
    shopColor = '#007bff'
}) => {
    const [availableFlavors, setAvailableFlavors] = useState([]);
    const [selectedFlavors, setSelectedFlavors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show && product) {
            loadFlavors();
            setSelectedFlavors([]);
            setError('');
        }
    }, [show, product]);

    const loadFlavors = async () => {
        setLoading(true);
        console.log('=== DEBUG CARGA DE GUSTOS ===');
        console.log('shopBranchId recibido:', shopBranchId);
        
        // Usar la misma variable que los demás servicios o detectar entorno automáticamente
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 
                       (window.location.hostname === 'localhost' || window.location.hostname.includes('localhost') 
                        ? 'http://localhost:8001' 
                        : 'https://mipiace-back.ucoode.com.ar');
        
        console.log('BASE_URL seleccionado:', baseUrl);
        
        try {
            if (!shopBranchId) {
                console.error('ERROR: ID de sucursal no disponible');
                setError('ID de sucursal no disponible');
                return;
            }

            const url = `${baseUrl}/api/flavors/public/v1`;
            console.log('URL completa que se va a llamar:', url);
            console.log('Parámetros:', { shopBranchId: shopBranchId });

            // Usar endpoint público sin autenticación
            const response = await axios.get(url, {
                params: { shopBranchId: shopBranchId }
            });
            
            console.log('Respuesta exitosa del servidor:', response);
            console.log('Datos de gustos recibidos:', response.data);
            console.log('Cantidad de gustos:', response.data?.length || 0);
            
            setAvailableFlavors(response.data || []);
        } catch (error) {
            console.error('ERROR COMPLETO al cargar gustos:', error);
            console.error('Error response:', error.response);
            console.error('Error message:', error.message);
            console.error('Error config:', error.config);
            
            let errorMessage = 'Error al cargar los gustos disponibles';
            if (error.response) {
                errorMessage = `Error ${error.response.status}: ${error.response.data?.message || error.response.statusText}`;
            } else if (error.request) {
                errorMessage = 'No se pudo conectar con el servidor';
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleFlavorToggle = (flavor) => {
        const isSelected = selectedFlavors.find(f => f.id === flavor.id);
        
        if (isSelected) {
            // Remover sabor
            setSelectedFlavors(selectedFlavors.filter(f => f.id !== flavor.id));
        } else {
            // Agregar sabor (respetando el máximo)
            if (selectedFlavors.length < maxFlavors) {
                setSelectedFlavors([...selectedFlavors, flavor]);
            }
        }
    };

    const handleConfirm = () => {
        if (selectedFlavors.length === 0) {
            setError('Debe seleccionar al menos un gusto');
            return;
        }

        if (selectedFlavors.length > maxFlavors) {
            setError(`No puede seleccionar más de ${maxFlavors} gusto${maxFlavors > 1 ? 's' : ''}`);
            return;
        }

        // Crear producto con gustos seleccionados
        const productWithFlavors = {
            ...product,
            selectedFlavors: selectedFlavors,
            flavorInfo: selectedFlavors.map(f => f.name).join(', ')
        };

        onConfirm(productWithFlavors);
        onHide();
    };

    const handleCancel = () => {
        setSelectedFlavors([]);
        setError('');
        onHide();
    };

    // Función para generar estilos dinámicos basados en el color del shop
    const getShopColorStyles = () => {
        return {
            headerGradient: `linear-gradient(135deg, ${shopColor} 0%, ${adjustColorBrightness(shopColor, -20)} 100%)`,
            primaryColor: shopColor,
            primaryHover: adjustColorBrightness(shopColor, -10),
            primaryLight: adjustColorBrightness(shopColor, 90),
            primaryLighter: adjustColorBrightness(shopColor, 95)
        };
    };

    // Función para ajustar el brillo de un color
    const adjustColorBrightness = (hex, percent) => {
        const num = parseInt(hex.replace("#", ""), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
    };

    // Agrupar gustos por categoría
    const flavorsByCategory = availableFlavors.reduce((acc, flavor) => {
        const categoryName = flavor.flavorCategoryName || 'Sin categoría';
        if (!acc[categoryName]) {
            acc[categoryName] = [];
        }
        acc[categoryName].push(flavor);
        return acc;
    }, {});

    const shopStyles = getShopColorStyles();
    
    return (
        <Modal show={show} onHide={handleCancel} size="lg" centered className="flavor-selection-modal">
            <Modal.Header closeButton style={{ background: shopStyles.headerGradient }}>
                <Modal.Title>
                    🍨 Seleccionar Gustos - {product?.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                                    <div className="selected-flavors-container">
                        <p className="text-muted mb-2">
                            Selecciona hasta <strong>{maxFlavors}</strong> gusto{maxFlavors > 1 ? 's' : ''} para tu {product?.name}
                        </p>
                        <div className="d-flex flex-wrap gap-2">
                            {selectedFlavors.map(flavor => (
                                <span 
                                    key={flavor.id} 
                                    className="badge fs-6"
                                    style={{ 
                                        background: `linear-gradient(135deg, ${shopStyles.primaryColor} 0%, ${shopStyles.primaryHover} 100%)`,
                                        border: `1px solid ${shopStyles.primaryColor}`
                                    }}
                                >
                                    {flavor.name}
                                    <button 
                                        type="button" 
                                        className="btn-close btn-close-white ms-2" 
                                        style={{ fontSize: '0.6em' }}
                                        onClick={() => handleFlavorToggle(flavor)}
                                    ></button>
                                </span>
                            ))}
                        </div>
                    </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Cargando gustos...</span>
                        </div>
                        <p>Cargando gustos disponibles...</p>
                    </div>
                ) : (
                    <div className="flavor-selection">
                        {Object.entries(flavorsByCategory).map(([categoryName, flavors]) => (
                            <div key={categoryName} className="flavor-category">
                                <h6 style={{ borderBottomColor: shopStyles.primaryColor }}>
                                    {categoryName}
                                </h6>
                                <div className="row">
                                    {flavors.map(flavor => {
                                        const isSelected = selectedFlavors.find(f => f.id === flavor.id);
                                        const canSelect = selectedFlavors.length < maxFlavors || isSelected;
                                        
                                        return (
                                            <div key={flavor.id} className="col-md-6 col-lg-4 mb-2">
                                                <div 
                                                    className={`card h-100 flavor-card ${isSelected ? 'selected' : ''} ${!canSelect ? 'disabled' : ''}`}
                                                    style={{
                                                        borderColor: isSelected ? shopStyles.primaryColor : undefined,
                                                        background: isSelected ? `linear-gradient(135deg, ${shopStyles.primaryLighter} 0%, ${shopStyles.primaryLight} 100%)` : undefined
                                                    }}
                                                    onClick={() => canSelect && handleFlavorToggle(flavor)}
                                                >
                                                    <div className="card-body">
                                                        <div className="d-flex align-items-center">
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => canSelect && handleFlavorToggle(flavor)}
                                                                disabled={!canSelect}
                                                                className="me-2"
                                                                style={{
                                                                    '--bs-form-check-bg': isSelected ? shopStyles.primaryColor : undefined,
                                                                    '--bs-form-check-border-color': isSelected ? shopStyles.primaryColor : undefined
                                                                }}
                                                            />
                                                            <div>
                                                                <div className="flavor-name-modal">
                                                                    {flavor.name}
                                                                </div>
                                                                {flavor.description && (
                                                                    <div className="flavor-description">
                                                                        {flavor.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {availableFlavors.length === 0 && !loading && (
                    <Alert variant="warning">
                        No hay gustos disponibles en este momento.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCancel}>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirm}
                    disabled={selectedFlavors.length === 0 || loading}
                    style={{
                        background: `linear-gradient(135deg, ${shopStyles.primaryColor} 0%, ${shopStyles.primaryHover} 100%)`,
                        border: `1px solid ${shopStyles.primaryColor}`,
                        boxShadow: `0 2px 8px ${shopStyles.primaryColor}40`
                    }}
                >
                    Agregar al Carrito ({selectedFlavors.length}/{maxFlavors})
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FlavorSelectionModal;